/**
 * recruitLogic - every recruiter-facing word on the recruitment list ("the sheet",
 * docs/plans/crm-recruitment-list-plan.md), and the pure math behind it: the needs-first sort,
 * the column model, the keyboard machine, the refetch merge, the 2,000-row cap, the rows handed
 * to the Next-up bar ("Work through"), the one rule that switches the attempt menu's tries off (the
 * sheet and the bar both ask it) and the bulk plan.
 *
 * React-free and axios-free. Never import ../lib or ./api here: both reach the api client, and
 * this module's tests must need no mocks (the workQueueLogic / outreachLogic precedent). The words
 * for a waiting row live in workQueueLogic (waitingLabel), so the sheet and the bar can never word
 * the same row two ways.
 */
import { contactName, studyInternalLabel, type StudyLabelParts } from '../types';
import { ownerWritePath } from '../lib';
import { DETECTOR_LABEL } from '../pages/safetyNetLane';
import {
  clockWords,
  dayWords,
  reasonTier,
  waitingLabel,
  type WorkQueueItem,
  type WorkQueueReason,
  type WorkQueueStudyRef,
  type WorkQueueTier,
} from '../workQueueLogic';
import type {
  AnswerColumnKey,
  RecruitAnswer,
  RecruitAnswerColumn,
  RecruitAttemptEffect,
  RecruitAttemptOutcome,
  RecruitAttemptResponse,
  RecruitNoteResponse,
  RecruitReason,
  RecruitReasonDetail,
  RecruitRow,
  RecruitSort,
  SheetColumn,
  SheetColumnKey,
  SheetColumnPrefs,
} from './types';

// ---------------------------------------------------------------------------------------------
// The row's recruiter (migration 20260929190000; owner request 2026-09-29)
// ---------------------------------------------------------------------------------------------

/**
 * The recruiter for this lead ON THIS STUDY: the study row's own. A lead on two studies can have
 * two; on the lead's focus study it is their main recruiter. An API that predates the migration
 * sends no field, and then the lead's main recruiter is the only one there is.
 */
export function rowOwnerId(row: RecruitRow): string | null {
  const own = row.membership?.owner_user_id;
  return own !== undefined ? own ?? null : row.contact?.owner_user_id ?? null;
}

/** True when this row's study is the one the lead is being worked for (their focus). */
export function rowIsFocus(row: RecruitRow): boolean {
  const focus = row.contact?.primary_study_id;
  return !!focus && focus === row.membership?.study_id;
}

/** The row after its study's recruiter changed: on the focus study the lead's main one moves too. */
export function withRowOwner(row: RecruitRow, owner: string | null): RecruitRow {
  return {
    ...row,
    membership: { ...row.membership, owner_user_id: owner },
    contact: rowIsFocus(row) ? { ...row.contact, owner_user_id: owner } : row.contact,
  };
}

// ---------------------------------------------------------------------------------------------
// Limits (mirror backend services/crmRecruitList.js)
// ---------------------------------------------------------------------------------------------

/** Rows per request: the contacts endpoint's page, and the most the rows route serves (RECRUIT_PAGE_MAX). */
export const RECRUIT_PAGE_SIZE = 200;
/** The sheet's ceiling (RECRUIT_ROW_CAP): past it the page says so instead of loading on. */
export const RECRUIT_ROW_CAP = 2000;
/** The sticky note's length bound (RECRUIT_NOTE_MAX). */
export const RECRUIT_NOTE_MAX = 2000;
/** Answer columns one study can show (decision 7). */
export const MAX_ANSWER_COLUMNS = 6;

// ---------------------------------------------------------------------------------------------
// Statuses (restated from lib, which reaches the api client; the tests pin them equal)
// ---------------------------------------------------------------------------------------------

/**
 * The per-study status vocabulary in funnel order: lib MEMBERSHIP_STATUSES (the contact pipeline,
 * then the two membership-only terminals).
 */
export const MEMBERSHIP_STATUS_ORDER: readonly string[] = [
  'new', 'contacting', 'qualified_pending_records', 'ready_to_schedule', 'scheduled', 'screened',
  'randomized', 'screen_failed', 'not_eligible', 'not_interested', 'unreachable',
  'completed', 'discontinued',
];

/**
 * The terminal-negative dispositions (lib FINAL_DISPOSITIONS, backend
 * DISPOSITION_NEGATIVE_STATUSES): a move into one needs a reason, asked once in bulk.
 */
export const REASON_REQUIRED_STATUSES: readonly string[] = [
  'screen_failed', 'not_eligible', 'not_interested', 'unreachable',
];

/** Closed on this study: the four dispositions, randomized, and the membership-only completed / discontinued. */
export const TERMINAL_STATUSES: readonly string[] = [
  ...REASON_REQUIRED_STATUSES, 'randomized', 'completed', 'discontinued',
];

/** A row closed on this study: greyed in place until a refresh (decision 6), never worked through. */
export function isTerminalStatus(status: string | null | undefined): boolean {
  return typeof status === 'string' && TERMINAL_STATUSES.includes(status);
}

/** Still in play on this study: one of the considering statuses (an unknown status is not). */
export function isOpenOnStudy(status: string | null | undefined): boolean {
  return typeof status === 'string' && MEMBERSHIP_STATUS_ORDER.includes(status) && !isTerminalStatus(status);
}

// ---------------------------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------------------------

/** Own-property lookup, so a key from the server can never reach Object.prototype. */
function own<T>(map: Record<string, T>, key: string | null | undefined): T | undefined {
  return key != null && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

const timeOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

// ---------------------------------------------------------------------------------------------
// The reason cell (Part A section 5)
// ---------------------------------------------------------------------------------------------

/** "40 min", "2 h", "3 d": how far `iso` is from now, either way. Null when unknown. */
export function spanWords(iso: string | null | undefined, now: Date = new Date()): string | null {
  const t = timeOf(iso);
  if (t === null) return null;
  const diff = Math.abs(now.getTime() - t);
  const m = Math.round(diff / 60e3);
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.round(diff / 3600e3);
  if (h < 48) return `${h} h`;
  return `${Math.round(diff / 86400e3)} d`;
}

/**
 * "try 3 of 6": numbers the try that is DUE (attempts already made + 1), capped at the ceiling,
 * the rule of the Callbacks card's "Attempt N of M" and outreach tryOf. Null without a count.
 */
export function tryWords(attempts: number | null | undefined, max: number | null | undefined): string | null {
  if (typeof attempts !== 'number' || !Number.isFinite(attempts)) return null;
  const made = Math.max(0, Math.floor(attempts));
  if (typeof max === 'number' && Number.isFinite(max) && max >= 1) {
    const ceiling = Math.floor(max);
    return `try ${Math.min(made + 1, ceiling)} of ${ceiling}`;
  }
  return `try ${made + 1}`;
}

/** The safety net's detectors, short enough for a cell ("Left behind: ghosted 4 d"). */
const DETECTOR_WORDS: Record<string, string> = {
  ghosted_engaged: 'ghosted',
  reply_owed: 'reply owed',
  assigned_idle: 'idle',
  stage_sla: 'stuck in stage',
  missed_callback: 'missed callback',
  stuck_review: 'review stuck',
  stalled_conversation: 'agent stalled',
  owner_absent_uncovered: 'owner out, uncovered',
};

function detectorWord(detector: string | null | undefined): string | null {
  const short = own(DETECTOR_WORDS, detector);
  if (short) return short;
  const label = own(DETECTOR_LABEL, detector);
  return label ? label.toLowerCase() : null;
}

export interface ReasonWordsOptions {
  /** The viewer: a booked call with someone else names them ("Callback 3:15 PM - Dana"). */
  meId?: string | null;
  /** A user id to a display name (the team list). */
  nameOf?: (userId: string) => string | null | undefined;
  /** The study's cadence ceiling, when the reason carries none (RecruitStudy.max_attempts). */
  maxAttempts?: number | null;
  /** Renders a moment; defaults to workQueueLogic clockWords ("3:15 PM"). Injected in tests. */
  formatTime?: (iso: string) => string;
  /** Renders a day; defaults to workQueueLogic dayWords ("Fri"). Injected in tests. */
  formatDay?: (iso: string) => string;
}

/** " · Dana Reyes" when the booked call is someone else's, else nothing. */
function withWhom(assignee: string | null | undefined, opts: ReasonWordsOptions): string {
  if (!assignee || assignee === opts.meId) return '';
  const name = opts.nameOf ? opts.nameOf(assignee) : null;
  return ` · ${name || 'a teammate'}`;
}

/**
 * The Reason cell: why the row is where it is, in Part A section 5's words ("Replied 2 h ago,
 * waiting on us", "Callback 3:15 PM", "New lead, came in 3 h ago", "Wrote 2 d ago, gone quiet",
 * "Left behind: ghosted 4 d", "Never contacted, came in 12 d ago", "No answer yet, try 3 of 6",
 * "Last wrote 45 d ago, follow-up due", "No activity 6 d", "Next try Fri", "Snoozed until Mon",
 * "Agent texting"). A text reply and a booked callback belong to the PERSON, so a lead on two
 * sheets reads the same on both.
 */
export function reasonWords(reason: RecruitReason, now: Date = new Date(), opts: ReasonWordsOptions = {}): string {
  const detail: RecruitReasonDetail = reason.detail || {};
  const span = spanWords(reason.at, now);
  const formatTime = opts.formatTime || ((iso: string) => clockWords(iso, now));
  const formatDay = opts.formatDay || ((iso: string) => dayWords(iso, now));
  switch (reason.kind) {
    case 'needs_reply':
      return span ? `Replied ${span} ago, waiting on us` : 'Replied, waiting on us';
    case 'callback_due': {
      const t = timeOf(reason.at);
      let words = 'Callback due';
      if (t !== null && reason.at) {
        words = t <= now.getTime() ? `Callback overdue ${span}` : `Callback ${formatTime(reason.at)}`;
      }
      return words + withWhom(detail.assignee_user_id, opts);
    }
    case 'agent_review':
      return 'Agent needs your decision';
    case 'new_lead': {
      // A hot new lead (attention tier 1): came in, or re-submitted a form, within the window.
      const head = span ? `New lead, came in ${span} ago` : 'New lead';
      const tries = detail.attempt ? tryWords(detail.attempt, detail.max_attempts ?? opts.maxAttempts) : null;
      return tries ? `${head}, ${tries}` : head;
    }
    case 'conversation': {
      // A live conversation gone quiet (attention tier 1): keep it going.
      const wrote = spanWords(detail.last_inbound_at, now);
      return wrote ? `Wrote ${wrote} ago, gone quiet` : 'Conversation gone quiet';
    }
    case 'not_contacted':
      return span ? `Never contacted, came in ${span} ago` : 'Never contacted';
    case 'safety_net': {
      const word = detectorWord(detail.detector);
      const head = word ? `Left behind: ${word}` : 'Left behind';
      return span ? `${head} ${span}` : head;
    }
    case 'follow_up': {
      // Keep trying (attention tier 2): nobody is waiting on us. Someone who wrote to us once is
      // never out of tries (the cadence never exhausts an engaged lead), so no "of 6" for them.
      const wrote = spanWords(detail.last_inbound_at, now);
      if (wrote) return `Last wrote ${wrote} ago, follow-up due`;
      const tries = tryWords(detail.attempt, detail.max_attempts ?? opts.maxAttempts);
      return tries ? `No answer yet, ${tries}` : 'No answer yet';
    }
    case 'stale':
      return span ? `No activity ${span}` : 'No recent activity';
    case 'waiting': {
      const words = waitingLabel(detail.why, reason.at, { now, formatTime, formatDay });
      return detail.why === 'callback_later' ? words + withWhom(detail.assignee_user_id, opts) : words;
    }
    case 'open':
      return 'Nothing due';
    default:
      return 'Needs attention';
  }
}

/**
 * The row's attention tier (backend services/crmAttention.js, owner 2026-09-28): 1 = needs action
 * now (someone waiting on us, a live conversation, a hot new lead), 2 = keep trying (due, but
 * nobody is waiting), 3 = nothing due. The server's own tier when it sent one; else the kind's
 * (an older server: every due kind was one tier then, and reads as needs action now).
 */
export function rowTier(reason: Pick<RecruitReason, 'kind' | 'tier'> | null | undefined): number {
  return reasonTier(reason);
}

/**
 * A row that needs action NOW: tier 1. The "Needs action now" filter keeps exactly these, the
 * picker's number counts them, and Work through serves them first.
 */
export function needsAction(reason: Pick<RecruitReason, 'kind' | 'tier'> | null | undefined): boolean {
  return !!reason && rowTier(reason) === 1;
}

/** A row to KEEP TRYING: tier 2 (due, but nobody is waiting on us). The "Keep trying" filter keeps these. */
export function keepTrying(reason: Pick<RecruitReason, 'kind' | 'tier'> | null | undefined): boolean {
  return !!reason && rowTier(reason) === 2;
}

/** A row with something due, in either tier (everything but waiting and open). */
export function isDue(reason: Pick<RecruitReason, 'kind' | 'tier'> | null | undefined): boolean {
  return !!reason && rowTier(reason) !== 3;
}

/** The banner at the 2,000-row ceiling (plan section 9), or null below it. */
export function capMessage(count: number, cap: number = RECRUIT_ROW_CAP): string | null {
  if (!Number.isFinite(count) || count < cap) return null;
  return `Showing the first ${cap.toLocaleString()} leads. Narrow the list with the filters or search to see the rest.`;
}

/**
 * The note cell's conflict line (409 note_changed): "Edited by Dana Reyes just now", or
 * "Cleared by ..." when the teammate emptied it. Shown before the recruiter chooses to overwrite.
 */
export function noteEditedLine(
  current: Pick<RecruitNoteResponse, 'note' | 'at' | 'by_name'> | null | undefined,
  now: Date = new Date(),
): string {
  const who = (current?.by_name || '').trim() || 'a teammate';
  const verb = current?.note && current.note.trim() ? 'Edited' : 'Cleared';
  const at = current?.at;
  const t = timeOf(at);
  if (t === null) return `${verb} by ${who}`;
  if (now.getTime() - t < 60e3) return `${verb} by ${who} just now`;
  return `${verb} by ${who} ${spanWords(at, now)} ago`;
}

// ---------------------------------------------------------------------------------------------
// The attempt menu's words (the OutcomeControl split-button shape, rebuilt for the attempt route)
// ---------------------------------------------------------------------------------------------

/** Menu order; `primary` is the split button's main half, `confirm` asks first (useConfirm). */
export const ATTEMPT_MENU: ReadonlyArray<{ value: RecruitAttemptOutcome; label: string; primary?: boolean; confirm?: boolean }> = [
  { value: 'no_answer', label: 'No answer', primary: true },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'talked', label: 'Talked' },
  { value: 'call_back', label: 'Call back at…' },
  { value: 'try_later', label: 'Try later…' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'asked_to_stop', label: 'Asked us to stop', confirm: true },
];

export const ATTEMPT_LABELS: Record<RecruitAttemptOutcome, string> = {
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  talked: 'Talked',
  call_back: 'Call back',
  try_later: 'Try later',
  wrong_number: 'Wrong number',
  asked_to_stop: 'Asked us to stop',
};

/** What the tries rule reads about a lead: from a sheet row, or from a study session's queue item. */
export interface TriesGateFacts {
  /** Merged into another record (contacts.merged_into_id set). */
  merged: boolean;
  /** Archived (contacts.archived_at set). */
  archived: boolean;
  /** The status on this study (crm.contact_study_statuses.status). */
  status: string | null | undefined;
}

/**
 * Why the tries (No answer ... Try later, and booking a call back) are unavailable for a lead, or
 * null when they are available. Fails closed, the display rule of crmLeadLiveness (plan B9): a
 * merged or archived lead, or one closed (or in an unknown state) on this study. Wrong number and
 * Asked us to stop stay available: they are recorded on the person even after the row closed. The
 * sheet (AttemptsCell triesUnavailableReason) and the Next-up bar (studyItemTriesReason) both ask
 * here, so the two never disagree about the same lead.
 */
export function triesOffReason(lead: TriesGateFacts): string | null {
  if (lead.merged) return 'This lead was merged into another record.';
  if (lead.archived) return 'Archived: unarchive the lead to log a call.';
  if (!isOpenOnStudy(lead.status)) return 'Closed on this study: change the status to log a call.';
  return null;
}

/**
 * The confirmation line after an attempt lands, built ONLY from the server's `effect`, so it
 * never claims more than happened (the workQueueLogic outcomeConfirmation rule). `slotStart` is
 * the callback time the recruiter picked (the effect carries only the callback's id).
 */
export function attemptConfirmation(
  outcome: RecruitAttemptOutcome,
  res: RecruitAttemptResponse | null | undefined,
  opts: {
    maxAttempts?: number | null;
    slotStart?: string | null;
    now?: Date;
    formatTime?: (iso: string) => string;
    formatDay?: (iso: string) => string;
  } = {},
): string {
  const now = opts.now ?? new Date();
  const formatTime = opts.formatTime || ((iso: string) => clockWords(iso, now));
  const formatDay = opts.formatDay || ((iso: string) => dayWords(iso, now));
  const effect: RecruitAttemptEffect = res?.effect || {};
  const tagged = Array.isArray(effect.tags) && effect.tags.length > 0;
  const label = own(ATTEMPT_LABELS, outcome) || 'Attempt';
  switch (outcome) {
    case 'no_answer':
    case 'voicemail':
    case 'talked': {
      const parts = [`${label} logged.`];
      if (typeof effect.attempt_count === 'number') {
        const max = opts.maxAttempts;
        parts.push(typeof max === 'number' && max >= 1
          ? `Attempt ${effect.attempt_count} of ${Math.floor(max)}.`
          : `Attempt ${effect.attempt_count}.`);
      }
      if (effect.next_action_at) parts.push(`Next try ${formatDay(effect.next_action_at)}.`);
      if (effect.agent_paused) parts.push('The texting agent is paused for them.');
      return parts.join(' ');
    }
    case 'call_back':
      if (!effect.callback_id) return 'Call back logged.';
      return opts.slotStart ? `Call back booked for ${formatTime(opts.slotStart)}.` : 'Call back booked.';
    case 'try_later':
      return effect.snoozed_until
        ? `Snoozed until ${formatDay(effect.snoozed_until)}. A text from them brings them back sooner.`
        : 'Try later logged.';
    case 'wrong_number':
      return tagged ? 'Marked as a wrong number.' : 'Wrong number logged.';
    case 'asked_to_stop':
      if (!effect.opted_out) return 'Asked us to stop logged.';
      return tagged ? 'Opted out of texts and marked Do not contact.' : 'Opted out of texts.';
    default:
      return 'Logged.';
  }
}

// ---------------------------------------------------------------------------------------------
// The column model (Part A section 4; per person, per study, per device)
// ---------------------------------------------------------------------------------------------

/** Part A section 4 order. Select and Name are pinned first and never hidden. */
export const DEFAULT_COLUMNS: readonly SheetColumn[] = [
  { key: 'select', label: 'Select', width: 40, editable: false, hideable: false, sortable: false },
  { key: 'name', label: 'Name', width: 200, editable: false, hideable: false, sortable: true },
  { key: 'phone', label: 'Phone', width: 136, editable: false, hideable: true, sortable: true },
  { key: 'status', label: 'Status', width: 176, editable: true, hideable: true, sortable: true },
  { key: 'reason', label: 'Reason', width: 224, editable: false, hideable: true, sortable: true },
  { key: 'owner', label: 'Recruiter', width: 156, editable: true, hideable: true, sortable: true },
  { key: 'note', label: 'Note', width: 240, editable: true, hideable: true, sortable: true },
  { key: 'attempts', label: 'Attempts', width: 132, editable: true, hideable: true, sortable: true },
  { key: 'last_touch', label: 'Last touch', width: 208, editable: false, hideable: true, sortable: true },
  { key: 'callback', label: 'Callback', width: 152, editable: false, hideable: true, sortable: true },
  { key: 'came_in', label: 'Came in', width: 144, editable: false, hideable: true, sortable: true },
  { key: 'eligibility', label: 'Eligibility', width: 120, editable: false, hideable: true, sortable: true },
  { key: 'flags', label: 'Flags', width: 200, editable: false, hideable: true, sortable: false },
];

const PINNED_KEYS: readonly SheetColumnKey[] = ['select', 'name'];

/**
 * The Eligibility column's words. The column shows the verdict of THIS study's own landing form
 * (contact.study_eligibility, set by the server), never the contact-wide incoming_eligibility: a
 * verdict from a form the lead filled out for another study does not belong on this study's list
 * (owner, 2026-09-28). A lead who never filled out this study's form shows a dash.
 */
export const STUDY_ELIGIBILITY_TITLE = "What their answers on this study's form said";
export const NO_STUDY_FORM_TITLE = "They haven't filled out this study's form";

const ANSWER_PREFIX = 'answer:';

/** The column key of one of the study's answer columns. */
export const answerColumnKey = (questionKey: string): AnswerColumnKey => `${ANSWER_PREFIX}${questionKey}` as AnswerColumnKey;

export const isAnswerColumnKey = (key: string): key is AnswerColumnKey =>
  key.startsWith(ANSWER_PREFIX) && key.length > ANSWER_PREFIX.length;

/** The question_key an answer column shows. */
export const answerKeyOf = (key: AnswerColumnKey): string => key.slice(ANSWER_PREFIX.length);

/** The study's answer columns as sheet columns: deduped by key, at most six, admin order. */
function answerColumnsOf(answerColumns: readonly RecruitAnswerColumn[] | null | undefined): SheetColumn[] {
  const out: SheetColumn[] = [];
  const seen = new Set<string>();
  for (const a of answerColumns || []) {
    const qk = typeof a?.question_key === 'string' ? a.question_key.trim() : '';
    if (!qk || seen.has(qk)) continue;
    seen.add(qk);
    const label = typeof a.label === 'string' && a.label.trim() ? a.label.trim() : qk;
    out.push({ key: answerColumnKey(qk), label, width: 144, editable: false, hideable: true, sortable: true });
    if (out.length >= MAX_ANSWER_COLUMNS) break;
  }
  return out;
}

function normalizePrefs(prefs: SheetColumnPrefs | null | undefined): SheetColumnPrefs {
  const strings = (v: unknown): string[] =>
    (Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : []);
  return { order: strings(prefs?.order), hidden: strings(prefs?.hidden) };
}

/**
 * Every column in the person's order, with whether it shows: Select and Name first (pinned),
 * then the saved order (unknown keys ignored), then any column the saved order does not name
 * yet in its default place at the end (a new answer column appears without a reset).
 */
export function columnChoices(
  prefs: SheetColumnPrefs | null | undefined,
  answerColumns: readonly RecruitAnswerColumn[] | null | undefined = [],
): Array<SheetColumn & { visible: boolean }> {
  const all: SheetColumn[] = [...DEFAULT_COLUMNS.map((c) => ({ ...c })), ...answerColumnsOf(answerColumns)];
  const byKey = new Map<string, SheetColumn>();
  for (const c of all) byKey.set(c.key, c);
  const { order, hidden } = normalizePrefs(prefs);
  const hiddenSet = new Set(hidden);
  const placed = new Set<string>();
  const out: Array<SheetColumn & { visible: boolean }> = [];
  const place = (key: string): void => {
    const col = byKey.get(key);
    if (!col || placed.has(key)) return;
    placed.add(key);
    out.push({ ...col, visible: !col.hideable || !hiddenSet.has(key) });
  };
  PINNED_KEYS.forEach((k) => place(k));
  order.forEach((k) => place(k));
  all.forEach((c) => place(c.key));
  return out;
}

/** The columns the sheet renders, in order. */
export function visibleColumns(
  prefs: SheetColumnPrefs | null | undefined,
  answerColumns: readonly RecruitAnswerColumn[] | null | undefined = [],
): SheetColumn[] {
  return columnChoices(prefs, answerColumns)
    .filter((c) => c.visible)
    .map(({ visible, ...column }) => column);
}

/** Hide or show one column (Select and Name never hide; an unknown key changes nothing). */
export function toggleColumn(prefs: SheetColumnPrefs | null | undefined, key: SheetColumnKey): SheetColumnPrefs {
  const base = normalizePrefs(prefs);
  const builtIn = DEFAULT_COLUMNS.find((c) => c.key === key);
  if (builtIn ? !builtIn.hideable : !isAnswerColumnKey(key)) return base;
  const hidden = base.hidden.includes(key) ? base.hidden.filter((k) => k !== key) : [...base.hidden, key];
  return { order: base.order, hidden };
}

/** Move one column `delta` places (negative = left); nothing moves ahead of Select and Name. */
export function moveColumn(
  prefs: SheetColumnPrefs | null | undefined,
  key: SheetColumnKey,
  delta: number,
  answerColumns: readonly RecruitAnswerColumn[] | null | undefined = [],
): SheetColumnPrefs {
  const base = normalizePrefs(prefs);
  const keys: string[] = columnChoices(base, answerColumns).map((c) => c.key);
  const from = keys.indexOf(key);
  if (from < 0 || PINNED_KEYS.includes(key)) return base;
  const to = Math.max(PINNED_KEYS.length, Math.min(keys.length - 1, from + Math.trunc(delta)));
  if (!Number.isFinite(to) || to === from) return base;
  keys.splice(from, 1);
  keys.splice(to, 0, key);
  return { order: keys, hidden: base.hidden };
}

/** Read the stored choice (pref crm.recruit.columns.<studyId>); anything unreadable is no choice. */
export function parseColumnPrefs(raw: string | null | undefined): SheetColumnPrefs | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return normalizePrefs(parsed as SheetColumnPrefs);
  } catch {
    return null;
  }
}

export const serializeColumnPrefs = (prefs: SheetColumnPrefs): string =>
  JSON.stringify(normalizePrefs(prefs));

// ---------------------------------------------------------------------------------------------
// Sorting: needs-first by default (Part A section 5), or by a clicked header
// ---------------------------------------------------------------------------------------------

export interface CompareOptions {
  /** A user id to a display name, for sorting by recruiter. */
  nameOf?: (userId: string) => string | null | undefined;
}

const ELIGIBILITY_ORDER: readonly string[] = ['eligible', 'needs_review', 'pending', 'not_eligible'];

type SortValue = number | string;

/** Position in a known list; unknown values after the known ones; blanks (null) last. */
const indexIn = (list: readonly string[], v: string | null | undefined): number | null => {
  if (!v) return null;
  const i = list.indexOf(v);
  return i < 0 ? list.length : i;
};

function answerSortValue(a: RecruitAnswer | undefined): SortValue | null {
  if (!a || a.value === null || a.value === undefined || a.value === '') return null;
  if (typeof a.value === 'number') return Number.isFinite(a.value) ? a.value : null;
  if (typeof a.value === 'boolean') return a.value ? 1 : 0;
  return String(a.value);
}

function sortValue(row: RecruitRow, key: SheetColumnKey, opts: CompareOptions): SortValue | null {
  switch (key) {
    case 'name': {
      const n = contactName(row.contact);
      return n === '(no name)' ? null : n;
    }
    case 'phone':
      return row.contact.phone_e164 || null;
    case 'status':
      return indexIn(MEMBERSHIP_STATUS_ORDER, row.membership.status);
    case 'owner': {
      const id = rowOwnerId(row);
      if (!id) return null;
      return (opts.nameOf && opts.nameOf(id)) || id;
    }
    case 'note': {
      const note = (row.membership.recruit_note || '').trim();
      return note || null;
    }
    case 'attempts':
      return typeof row.contact.attempt_count === 'number' ? row.contact.attempt_count : null;
    case 'last_touch':
      return timeOf(row.contact.last_comm?.occurred_at);
    case 'callback':
      return timeOf(row.callback?.scheduled_at);
    case 'came_in':
      return timeOf(row.membership.first_considered_at);
    case 'eligibility':
      return indexIn(ELIGIBILITY_ORDER, row.contact.study_eligibility);
    default:
      return isAnswerColumnKey(key) ? answerSortValue(own(row.answers || {}, answerKeyOf(key))) : null;
  }
}

function compareValues(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
  }
  return typeof a === 'number' ? -1 : 1;
}

/** Blanks last whichever way the column runs; otherwise `sign` orders the pair. */
function compareNullable(a: SortValue | null, b: SortValue | null, sign: number): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return sign * compareValues(a, b);
}

const rankValue = (row: RecruitRow): number | null => {
  const r = row.reason?.rank;
  return typeof r === 'number' && Number.isFinite(r) ? r : null;
};

/** The needs-first order: rank, then the due moment (blanks last), each in `sign` order. */
function compareByReason(a: RecruitRow, b: RecruitRow, sign: number): number {
  return compareNullable(rankValue(a), rankValue(b), sign)
    || compareNullable(timeOf(a.reason?.at), timeOf(b.reason?.at), sign);
}

/** The last word on every tie: the name, then the contact id, so the order is total. */
function tieBreak(a: RecruitRow, b: RecruitRow): number {
  const byName = contactName(a.contact).localeCompare(contactName(b.contact), undefined, { sensitivity: 'base' });
  if (byName !== 0) return byName;
  if (a.contact.id === b.contact.id) return 0;
  return a.contact.id < b.contact.id ? -1 : 1;
}

const isSortableKey = (key: SheetColumnKey): boolean =>
  isAnswerColumnKey(key) || DEFAULT_COLUMNS.some((c) => c.key === key && c.sortable);

/**
 * The sheet's comparator. The default (no sort, or the Reason column ascending) is needs-first:
 * rank ascending, then `reason.at` ascending with blanks last, then the name. A clicked header
 * sorts by that column (blanks last either way) and falls back to the needs-first order on ties.
 */
export function compareRows(
  sort?: RecruitSort | null,
  opts: CompareOptions = {},
): (a: RecruitRow, b: RecruitRow) => number {
  const sign = sort?.dir === 'desc' ? -1 : 1;
  const key = sort?.key;
  if (!key || key === 'reason' || !isSortableKey(key)) {
    const reasonSign = key === 'reason' ? sign : 1;
    return (a, b) => compareByReason(a, b, reasonSign) || tieBreak(a, b);
  }
  return (a, b) => compareNullable(sortValue(a, key, opts), sortValue(b, key, opts), sign)
    || compareByReason(a, b, 1)
    || tieBreak(a, b);
}

// ---------------------------------------------------------------------------------------------
// The keyboard machine (Sheet view; Part A section 3 step 8)
// ---------------------------------------------------------------------------------------------

/** The cursor: a row, a column (visible-column index), and whether that cell's editor is open. */
export interface CursorState {
  row: number;
  col: number;
  editing: boolean;
}

export type CursorKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Tab' | 'ShiftTab' | 'Home' | 'End';

const clampTo = (n: number, count: number): number => (count <= 0 ? 0 : Math.min(Math.max(0, n), count - 1));

/**
 * Move the cursor. Arrows move one cell and stop at the edges; Tab / Shift-Tab walk across the
 * row and wrap to the next / previous row (stopping at the first and last cells); Home / End jump
 * to the row's ends. While an editor is open the arrows belong to it (nothing moves); Tab closes
 * the editor and moves on, as a spreadsheet does.
 */
export function moveCursor(state: CursorState, key: CursorKey, rowCount: number, colCount: number): CursorState {
  if (state.editing && key !== 'Tab' && key !== 'ShiftTab') return state;
  const row = clampTo(state.row, rowCount);
  const col = clampTo(state.col, colCount);
  const lastCol = clampTo(colCount - 1, colCount);
  switch (key) {
    case 'ArrowUp':
      return { row: clampTo(row - 1, rowCount), col, editing: false };
    case 'ArrowDown':
      return { row: clampTo(row + 1, rowCount), col, editing: false };
    case 'ArrowLeft':
      return { row, col: clampTo(col - 1, colCount), editing: false };
    case 'ArrowRight':
      return { row, col: clampTo(col + 1, colCount), editing: false };
    case 'Home':
      return { row, col: 0, editing: false };
    case 'End':
      return { row, col: lastCol, editing: false };
    case 'Tab':
      if (col < lastCol) return { row, col: col + 1, editing: false };
      if (row < rowCount - 1) return { row: row + 1, col: 0, editing: false };
      return { row, col, editing: false };
    case 'ShiftTab':
      if (col > 0) return { row, col: col - 1, editing: false };
      if (row > 0) return { row: row - 1, col: lastCol, editing: false };
      return { row, col, editing: false };
    default:
      return state;
  }
}

/** Enter on a cell: open its editor when the column is editable (status, recruiter, note, attempts). */
export function enterCell(
  state: CursorState,
  columns: readonly Pick<SheetColumn, 'editable'>[],
  rowCount: number,
): CursorState {
  if (state.editing || rowCount <= 0) return state;
  const column = columns[state.col];
  return column && column.editable ? { ...state, editing: true } : state;
}

/** Esc (or a finished edit): close the editor, keep the cursor where it is. */
export function escape(state: CursorState): CursorState {
  return state.editing ? { ...state, editing: false } : state;
}

export type SheetKeyAction =
  | { type: 'move'; key: CursorKey }
  | { type: 'enter' }
  | { type: 'escape' }
  | { type: 'open' }
  | { type: 'search' };

/** The parts of a KeyboardEvent the sheet reads. */
export interface KeyLike {
  key: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

const MOVE_KEYS: ReadonlySet<string> = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

/**
 * What a key means on the sheet, or null for "not ours". A key with Ctrl, Cmd or Alt is never
 * taken (browser and system shortcuts stay theirs). While an editor is open only Esc and Tab are
 * the sheet's; every other key belongs to the editor, so typing "o" or "/" in a note is text.
 */
export function sheetKeyAction(e: KeyLike, editing: boolean): SheetKeyAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key === 'Escape') return { type: 'escape' };
  if (e.key === 'Tab') return { type: 'move', key: e.shiftKey ? 'ShiftTab' : 'Tab' };
  if (editing) return null;
  if (MOVE_KEYS.has(e.key)) return { type: 'move', key: e.key as CursorKey };
  if (e.key === 'Enter') return { type: 'enter' };
  if (e.key === 'o' && !e.shiftKey) return { type: 'open' };
  if (e.key === '/') return { type: 'search' };
  return null;
}

// ---------------------------------------------------------------------------------------------
// Loading and refetching (B7: a 60-second poll, and a refresh after every write)
// ---------------------------------------------------------------------------------------------

/**
 * The next page's offset while loading a sheet 200 at a time, or null to stop: at a short page,
 * when the server says the read is capped, or at the 2,000-row ceiling.
 */
export function nextPageOffset(
  offset: number,
  pageLength: number,
  capped: boolean,
  pageSize: number = RECRUIT_PAGE_SIZE,
  cap: number = RECRUIT_ROW_CAP,
): number | null {
  if (capped || pageLength <= 0 || pageLength < pageSize) return null;
  const next = offset + pageLength;
  return next >= cap ? null : next;
}

/** The cell whose editor is open while a refetch lands. */
export interface EditingCell {
  contactId: string;
  column: SheetColumnKey;
}

/** The fresh row with the field under edit kept as it was when the editor opened. */
function keepEditedField(current: RecruitRow, next: RecruitRow, column: SheetColumnKey): RecruitRow {
  switch (column) {
    case 'note':
      // Keeping recruit_note_at too is what makes a teammate's change meanwhile come back as the
      // 409 "Edited by ..." on save, instead of being overwritten without a word.
      return {
        ...next,
        membership: {
          ...next.membership,
          recruit_note: current.membership.recruit_note,
          recruit_note_at: current.membership.recruit_note_at,
          recruit_note_by: current.membership.recruit_note_by,
          recruit_note_by_name: current.membership.recruit_note_by_name,
        },
      };
    case 'status':
      return {
        ...next,
        membership: {
          ...next.membership,
          status: current.membership.status,
          status_changed_at: current.membership.status_changed_at,
          status_changed_by: current.membership.status_changed_by,
        },
      };
    case 'owner':
      return {
        ...next,
        contact: { ...next.contact, owner_user_id: current.contact.owner_user_id },
        membership: { ...next.membership, owner_user_id: current.membership.owner_user_id },
      };
    default:
      return next;
  }
}

/**
 * Fold a refetch into the sheet without moving anything under the recruiter:
 *   - rows still present stay where they are and adopt every fresh value, except the field whose
 *     editor is open (its draft keeps its footing, and a note changed meanwhile surfaces as the
 *     409 on save);
 *   - rows no longer present drop, unless their editor is open or `keep` names them (a row the
 *     recruiter just closed stays, greyed, until a manual refresh: decision 6);
 *   - rows new to the sheet are appended, in `compare` order (needs-first by default).
 * Pure: never mutates its inputs. A contact that appears twice in `fresh` counts once.
 */
export function mergeRefetch(
  current: readonly RecruitRow[],
  fresh: readonly RecruitRow[],
  editing: EditingCell | null | undefined,
  opts: { keep?: ReadonlySet<string> | null; compare?: (a: RecruitRow, b: RecruitRow) => number } = {},
): RecruitRow[] {
  const freshById = new Map<string, RecruitRow>();
  for (const row of fresh) {
    const id = row?.contact?.id;
    if (id && !freshById.has(id)) freshById.set(id, row);
  }
  const editingId = editing ? editing.contactId : null;
  const out: RecruitRow[] = [];
  const placed = new Set<string>();
  for (const row of current) {
    const id = row?.contact?.id;
    if (!id || placed.has(id)) continue;
    const next = freshById.get(id);
    if (next) {
      out.push(editing && id === editingId ? keepEditedField(row, next, editing.column) : next);
      placed.add(id);
    } else if (id === editingId || (opts.keep && opts.keep.has(id))) {
      out.push(row);
      placed.add(id);
    }
  }
  const added: RecruitRow[] = [];
  freshById.forEach((row, id) => {
    if (!placed.has(id)) added.push(row);
  });
  added.sort(opts.compare || compareRows());
  return out.concat(added);
}

// ---------------------------------------------------------------------------------------------
// Work through: the sheet's rows as Next-up queue items (plan B5)
// ---------------------------------------------------------------------------------------------

/** The study as a Next-up session names it: the internal title wins, the CRM's rule for recruiters. */
export function studyQueueRef(
  study: { id: string; slug: string; title?: string | null; internal_title?: string | null },
): WorkQueueStudyRef {
  return { id: study.id, slug: study.slug, title: studyInternalLabel(study) };
}

function queueReason(reason: RecruitReason, studyTitle: string | null): WorkQueueReason {
  const detail: RecruitReasonDetail = reason.detail || {};
  const out: WorkQueueReason = { kind: reason.kind, at: reason.at ?? null, rank: reason.rank, tier: rowTier(reason) };
  if (detail.last_inbound_at !== undefined) out.last_inbound_at = detail.last_inbound_at;
  if (detail.detector) {
    out.detector = detail.detector;
    out.detectors = [detail.detector];
  }
  if (typeof detail.attempt === 'number') out.attempt_count = detail.attempt;
  if (detail.why) out.why = detail.why;
  if (reason.kind === 'open') out.study_title = studyTitle;
  return out;
}

/**
 * A study session's queue item: the WorkQueueItem contract, every field as it was, plus the two
 * facts the Next-up bar gates the attempt menu's tries on the way the sheet does
 * (studyItemTriesReason). They are the row's as the sheet read it (Work through, or the session's
 * refill), and a stored session keeps them: items round-trip through sessionStorage as they are.
 */
export interface StudyQueueItem extends WorkQueueItem {
  /** The lead's status on this study (crm.contact_study_statuses.status); null when the row had none. */
  membership_status: string | null;
  /** The lead is archived (contacts.archived_at set): on the sheet only with Show archived. */
  archived: boolean;
}

/**
 * The rows on screen, in the sheet's order after its filters, as Next-up queue items (the item
 * contract workQueueLogic already renders, plus the study facts of StudyQueueItem). `tier` picks
 * the attention tier served (owner 2026-09-28: "urgent first, then offer the rest"): `now` (the
 * default) the needs-action-now rows, `later` the keep-trying rows, `all` both. Rows with nothing
 * due (`waiting`, `open`) ride along only when the recruiter asked for them (`includeWaiting`,
 * a session stored before the tiers: then every tier is served, as it was). A row closed on this
 * study is never served, and a lead appears once. The viewer-first ordering is startRowsState's
 * job.
 */
export function rowsToQueueItems(
  rows: readonly RecruitRow[],
  study: StudyLabelParts,
  opts: { includeWaiting?: boolean; tier?: WorkQueueTier | null } = {},
): StudyQueueItem[] {
  const tier: WorkQueueTier = opts.includeWaiting ? 'all' : opts.tier || 'now';
  const served = (reason: RecruitReason): boolean => {
    const t = rowTier(reason);
    if (t === 3) return !!opts.includeWaiting;
    return tier === 'all' || (tier === 'now' ? t === 1 : t === 2);
  };
  const studyTitle = studyInternalLabel(study) || null;
  const out: StudyQueueItem[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const c = row?.contact;
    if (!c || !c.id || !row.reason || seen.has(c.id)) continue;
    // A lead's first row decides for it: a later duplicate never slips into another tier's session.
    seen.add(c.id);
    if (isTerminalStatus(row.membership?.status)) continue;
    if (!served(row.reason)) continue;
    out.push({
      key: `${row.reason.kind}-${c.id}`,
      href: `/crm/contacts/${c.id}`,
      contact_id: c.id,
      name: contactName(c),
      lifecycle_status: c.lifecycle_status ?? null,
      // Whose lead it is on THIS study (the session is the study's).
      owner_user_id: rowOwnerId(row),
      study_title: studyTitle,
      // The bar's chip in a study session is THIS study's form, as on the sheet.
      incoming_eligibility: c.study_eligibility ?? null,
      reason: queueReason(row.reason, studyTitle),
      membership_status: row.membership?.status ?? null,
      archived: !!c.archived_at,
    });
  }
  return out;
}

/**
 * Why the Next-up bar's attempt menu cannot log a try for a study session's item: the sheet's rule
 * (triesOffReason) over the facts the item carries from its row. The rows read never returns a
 * merged lead (and the attempt route refuses one), so only the archived flag and the status on the
 * study are carried. An item without the facts fails closed, like an unknown status on the sheet.
 * The facts are the row's when the sheet was read: a lead closed or archived from its own page
 * after that keeps its tries in the bar (the attempt route itself refuses only a merged lead).
 */
export function studyItemTriesReason(item: WorkQueueItem): string | null {
  const facts: Partial<StudyQueueItem> = item;
  return triesOffReason({
    merged: false,
    archived: facts.archived === true,
    status: typeof facts.membership_status === 'string' ? facts.membership_status : null,
  });
}

// ---------------------------------------------------------------------------------------------
// Bulk edits (decision 10): the inbox's pattern, one write per row, sequential
// ---------------------------------------------------------------------------------------------

export type BulkAction =
  | { kind: 'owner'; owner_user_id: string | null }
  | { kind: 'status'; status: string };

/**
 * The body of one PATCH: { owner_user_id } for this study's recruiter
 * (PATCH /contacts/:id/studies/:studyId/owner; the server moves the lead's main recruiter when
 * this is their focus study), { status, ... } for the per-study status PATCH.
 */
export interface BulkBody {
  owner_user_id?: string | null;
  status?: string;
  reason_code?: string;
  disposition_note?: string;
}

export interface BulkWrite {
  contact_id: string;
  name: string;
  /**
   * The recruiter: /api/crm/contacts/<id>/studies/<studyId>/owner; the status on this study:
   * /api/crm/contacts/<id>/studies/<studyId>.
   */
  path: string;
  body: BulkBody;
}

export interface BulkPlan {
  action: BulkAction;
  /** In the order the rows were selected on screen; each lead once. */
  writes: BulkWrite[];
  /** Contact ids already at the target: nothing is sent for them. */
  unchanged: string[];
  /**
   * A status move into a terminal-negative disposition: collect the reason ONCE
   * (DispositionReasonModal, the criterion picker hidden) and add it with applyBulkReason.
   */
  requiresReason: boolean;
}

/**
 * The writes a bulk action makes, in order. Status goes through the per-study PATCH (so no study
 * confirmation: the study is the sheet) and the recruiter is THIS study's (lib ownerWritePath:
 * the lead's main one when this is their focus study); no body ever carries
 * randomized_conflict_acknowledged, so a 409 stays that row's refusal.
 */
export function buildBulkPlan(selection: readonly RecruitRow[], action: BulkAction): BulkPlan {
  const writes: BulkWrite[] = [];
  const unchanged: string[] = [];
  const seen = new Set<string>();
  for (const row of selection) {
    const id = row?.contact?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const name = contactName(row.contact);
    if (action.kind === 'owner') {
      const target = action.owner_user_id || null;
      if (rowOwnerId(row) === target) {
        unchanged.push(id);
        continue;
      }
      writes.push({
        contact_id: id,
        name,
        path: ownerWritePath(id, row.membership.study_id),
        body: { owner_user_id: target },
      });
    } else {
      if (row.membership.status === action.status) {
        unchanged.push(id);
        continue;
      }
      writes.push({
        contact_id: id,
        name,
        path: `/api/crm/contacts/${id}/studies/${row.membership.study_id}`,
        body: { status: action.status },
      });
    }
  }
  const requiresReason = action.kind === 'status'
    && REASON_REQUIRED_STATUSES.includes(action.status)
    && writes.length > 0;
  return { action, writes, unchanged, requiresReason };
}

/** The reason, collected once, on every status write (a recruiter plan is returned unchanged). */
export function applyBulkReason(plan: BulkPlan, reasonCode: string, note?: string | null): BulkPlan {
  if (plan.action.kind !== 'status') return plan;
  const extra: BulkBody = { reason_code: reasonCode };
  const trimmed = (note || '').trim();
  if (trimmed) extra.disposition_note = trimmed;
  return { ...plan, writes: plan.writes.map((w) => ({ ...w, body: { ...w.body, ...extra } })) };
}

export type BulkRowResult =
  | { contact_id: string; outcome: 'updated' }
  | { contact_id: string; outcome: 'refused'; study_title: string | null }
  | { contact_id: string; outcome: 'failed'; message: string | null };

/**
 * One row's result: no error is "updated"; the randomized guard's 409 is "refused" (reported,
 * never acknowledged in bulk); anything else is "failed", with the server's sentence when it sent one.
 */
export function bulkRowResult(contactId: string, error?: unknown): BulkRowResult {
  if (error === undefined || error === null) return { contact_id: contactId, outcome: 'updated' };
  const res = (error as { response?: { status?: number; data?: any } }).response;
  const data = res?.data;
  if (res?.status === 409 && data?.reason === 'randomized_conflict') {
    const title = data?.randomized_study?.title;
    return { contact_id: contactId, outcome: 'refused', study_title: typeof title === 'string' && title ? title : null };
  }
  return { contact_id: contactId, outcome: 'failed', message: typeof data?.error === 'string' ? data.error : null };
}

/** "14 updated, 1 refused: randomized in Migraine": every row reports, nothing is silent. */
export function bulkResultLine(results: readonly BulkRowResult[], unchanged: number = 0): string {
  let updated = 0;
  let failed = 0;
  const refusedTitles: Array<string | null> = [];
  for (const r of results) {
    if (r.outcome === 'updated') updated += 1;
    else if (r.outcome === 'refused') refusedTitles.push(r.study_title);
    else failed += 1;
  }
  const parts = [`${updated.toLocaleString()} updated`];
  if (unchanged > 0) parts.push(`${unchanged.toLocaleString()} already set`);
  if (refusedTitles.length > 0) {
    const titles = Array.from(new Set(refusedTitles.filter((t): t is string => !!t)));
    const where = titles.length === 1 ? `in ${titles[0]}` : titles.length > 1 ? 'in other studies' : 'in another study';
    parts.push(`${refusedTitles.length.toLocaleString()} refused: randomized ${where}`);
  }
  if (failed > 0) parts.push(`${failed.toLocaleString()} failed`);
  return parts.join(', ');
}
